import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Table, Button, Pagination, OverlayTrigger, Tooltip, Spinner, Form } from 'react-bootstrap';
import { FaFileExcel, FaSort, FaSortUp, FaSortDown, FaSearch, FaCity, FaFileInvoiceDollar } from 'react-icons/fa';
import moment from 'moment';
import AuthStorage from '~/Utils/AuthStorage';
import { CAD_CONTA_PREFEITURA } from '~/constant';
import styles from '../styles.module.css';

const TAMANHO_PAGINA = 50;

const formatarData = (valor) => {
  if (!valor) return '—';
  const d = moment(valor);
  return d.isValid() ? d.format('DD/MM/YYYY') : '—';
};

const movTipoClass = (movTipo) => {
  if (movTipo === 1) return styles.iptuRowMov1;
  if (movTipo === 2) return styles.iptuRowMov2;
  if (movTipo === 3) return styles.iptuRowMov3;
  if (movTipo === 4) return styles.iptuRowMov4;
  if (movTipo === 5) return styles.iptuRowMov5;
  return '';
};

// Replica PopupMenu1 do Delphi (UImovelCarne.pas — 6 itens no menu de contexto do DBGrid1).
const MENU_CONTEXTO_ITENS = [
  { id: 'carne-status',     label: 'Alterar Status Pagamento' },
  { id: 'opcao-pagamento',  label: 'Alterar Opção Pagamento' },
  { id: 'opcao-condominio', label: 'Alterar Opção Condomínio', requerCondominio: true },
  { id: 'cad-prefeitura',   label: 'Alterar Cadastro Prefeitura' },
  { id: 'ordem-pagamento',  label: 'Alterar Ordem de Pagamento' },
  { id: 'imprimir-rescisao', label: 'Imprimir Cálculo de Rescisão', requerRescisao: true },
];

// Réplica fiel de UImovelCarne.pas:ImoveisAfterScroll —
//   if movTipo=2 → enabled
//   else if movTipo=4 AND movTemRescisao='S' → enabled
//   else disabled
const podeImprimirRescisao = (imovel) => {
  if (!imovel) return false;
  if (imovel.mov_tipo === 2) return true;
  if (imovel.mov_tipo === 4 && imovel.mov_tem_rescisao === 'S') return true;
  return false;
};

const GridGestaoIPTU = ({
  imoveis,
  selecionados,
  onToggleSelecionado,
  onToggleTodos,
  habilitaCondominio,
  cidadeSelecionada,
  onAbrirModal,
  onVerDetalhes,
  onDebitosIPTU,
  onCadPrefeitura,
  onImprimirRescisao,
  onExportarExcel,
  totalRegistros,
  pagina,
  onMudarPagina,
  carregando,
  carregandoExcel,
  ordenacao,
  onOrdenacao,
}) => {
  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / TAMANHO_PAGINA));
  const todosSelecionados = imoveis.length > 0 && imoveis.every((i) => selecionados.includes(i.codigo_ativo));
  const podeCadPrefeitura = AuthStorage.temCaracteristica(CAD_CONTA_PREFEITURA);

  const [menuCtx, setMenuCtx] = useState(null); // { x, y, imovel }
  const [menuPos, setMenuPos] = useState(null);  // ajustado p/ caber no viewport
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuCtx) return undefined;
    const fechar = (ev) => {
      if (menuRef.current && menuRef.current.contains(ev.target)) return;
      setMenuCtx(null);
    };
    window.addEventListener('click', fechar);
    window.addEventListener('scroll', fechar, true);
    window.addEventListener('resize', fechar);
    return () => {
      window.removeEventListener('click', fechar);
      window.removeEventListener('scroll', fechar, true);
      window.removeEventListener('resize', fechar);
    };
  }, [menuCtx]);

  // Reposiciona o menu de contexto para que ele caiba no viewport: quando o
  // clique acontece perto da borda inferior ou direita da janela, o menu
  // ultrapassaria a tela e ficaria cortado. Mede após o primeiro paint e
  // ajusta top/left antes do próximo (useLayoutEffect roda síncrono pré-paint).
  useLayoutEffect(() => {
    if (!menuCtx) { if (menuPos !== null) setMenuPos(null); return; }
    if (!menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    const margem = 8;
    let top = menuCtx.y;
    let left = menuCtx.x;
    if (top + rect.height > window.innerHeight - margem) {
      top = Math.max(margem, window.innerHeight - rect.height - margem);
    }
    if (left + rect.width > window.innerWidth - margem) {
      left = Math.max(margem, window.innerWidth - rect.width - margem);
    }
    if (top !== menuCtx.y || left !== menuCtx.x) {
      setMenuPos({ top, left });
    } else {
      setMenuPos(null);
    }
  }, [menuCtx]);

  const abrirMenuContexto = (ev, item) => {
    ev.preventDefault();
    // Clique direito só abre o menu — não altera seleção. Seleção é
    // exclusiva do checkbox (clique esquerdo).
    setMenuCtx({ x: ev.clientX, y: ev.clientY, imovel: item });
  };

  const iconeOrdenacao = (campo) => {
    if (ordenacao.campo !== campo) return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    return ordenacao.direcao === 'asc'
      ? <FaSortUp style={{ marginLeft: 4 }} />
      : <FaSortDown style={{ marginLeft: 4 }} />;
  };

  const thStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', fontSize: 12 };

  const handleMenuItemClick = (itemMenu) => {
    const imovel = menuCtx ? menuCtx.imovel : null;
    setMenuCtx(null);
    if (itemMenu.id === 'opcao-condominio' && !habilitaCondominio) return;
    // Clique em item de menu NÃO altera a seleção — a seleção já foi
    // feita pelo clique direito sobre o registro (abrirMenuContexto).
    if (itemMenu.id === 'imprimir-rescisao') {
      if (!podeImprimirRescisao(imovel)) return;
      onImprimirRescisao && onImprimirRescisao(imovel);
      return;
    }
    onAbrirModal(itemMenu.id);
  };

  const renderizarPaginacao = () => {
    if (totalPaginas <= 1) return null;

    const itens = [];
    const maxBotoes = 5;

    itens.push(
      <Pagination.First key="first" onClick={() => onMudarPagina(1)} disabled={pagina === 1} />
    );
    itens.push(
      <Pagination.Prev key="prev" onClick={() => onMudarPagina(pagina - 1)} disabled={pagina === 1} />
    );

    let inicio = Math.max(1, pagina - Math.floor(maxBotoes / 2));
    let fim = Math.min(totalPaginas, inicio + maxBotoes - 1);
    if (fim - inicio < maxBotoes - 1) {
      inicio = Math.max(1, fim - maxBotoes + 1);
    }

    if (inicio > 1) itens.push(<Pagination.Ellipsis key="e1" disabled />);

    for (let i = inicio; i <= fim; i++) {
      itens.push(
        <Pagination.Item key={`p-${i}`} active={i === pagina} onClick={() => onMudarPagina(i)}>
          {i}
        </Pagination.Item>
      );
    }

    if (fim < totalPaginas) itens.push(<Pagination.Ellipsis key="e2" disabled />);

    itens.push(
      <Pagination.Next key="next" onClick={() => onMudarPagina(pagina + 1)} disabled={pagina >= totalPaginas} />
    );
    itens.push(
      <Pagination.Last key="last" onClick={() => onMudarPagina(totalPaginas)} disabled={pagina >= totalPaginas} />
    );

    return (
      <div className="d-flex justify-content-between align-items-center px-2 py-2 flex-wrap" style={{ borderTop: '1px solid #dee2e6', gap: 8 }}>
        <small className="text-muted">
          Mostrando {imoveis.length > 0 ? (pagina - 1) * TAMANHO_PAGINA + 1 : 0} até{' '}
          {Math.min(pagina * TAMANHO_PAGINA, totalRegistros)} de {totalRegistros} registros
        </small>
        <Pagination size="sm" className="mb-0">{itens}</Pagination>
      </div>
    );
  };

  return (
    <div>
      {/* Barra de ações fixas: Débitos IPTU + Cad. Prefeitura + Exportar Excel */}
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-2 p-2" style={{ gap: 8 }}>
        <div className="d-flex flex-wrap" style={{ gap: 8 }}>
          <OverlayTrigger
            placement="top"
            overlay={
              !cidadeSelecionada
                ? <Tooltip>Selecione uma cidade no filtro</Tooltip>
                : imoveis.length === 0
                  ? <Tooltip>Faça uma busca com resultados</Tooltip>
                  : <Tooltip>Lançar débitos IPTU para os imóveis filtrados</Tooltip>
            }
          >
            <span>
              <Button
                variant="warning"
                onClick={onDebitosIPTU}
                disabled={!cidadeSelecionada || imoveis.length === 0}
                style={{ pointerEvents: (!cidadeSelecionada || imoveis.length === 0) ? 'none' : 'auto' }}
              >
                <FaFileInvoiceDollar className="mr-1" /> Débitos IPTU
              </Button>
            </span>
          </OverlayTrigger>

          {podeCadPrefeitura && (
            <Button
              variant="info"
              onClick={onCadPrefeitura}
            >
              <FaCity className="mr-1" /> Cad. Conta Prefeitura
            </Button>
          )}
        </div>

        <Button
          variant="success"
          onClick={onExportarExcel}
          disabled={imoveis.length === 0 || carregandoExcel}
        >
          {carregandoExcel
            ? <Spinner animation="border" size="sm" className="mr-1" />
            : <FaFileExcel className="mr-1" />}
          Exportar Excel
        </Button>
      </div>

      {/* Barra de ações em lote (só aparece com seleção) */}
      {selecionados.length > 0 && (
        <div className="d-flex align-items-center flex-wrap gap-2 mb-2 p-2 bg-light rounded">
          <span className="text-muted mr-2" style={{ fontSize: 13 }}>
            <strong>{selecionados.length}</strong> selecionado(s)
          </span>
          <Button variant="outline-secondary" onClick={() => onAbrirModal('carne-status')}>
            Alterar Status Carnê
          </Button>
          <Button variant="outline-secondary" onClick={() => onAbrirModal('opcao-pagamento')}>
            Alterar Opção Pagamento
          </Button>
          {habilitaCondominio ? (
            <Button variant="outline-secondary" onClick={() => onAbrirModal('opcao-condominio')}>
              Alterar Opção Condomínio
            </Button>
          ) : (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Disponível apenas para empreendimentos Vertical (V) ou Horizontal (H)</Tooltip>}
            >
              <span>
                <Button variant="outline-secondary" disabled style={{ pointerEvents: 'none' }}>
                  Alterar Opção Condomínio
                </Button>
              </span>
            </OverlayTrigger>
          )}
          <Button variant="outline-secondary" onClick={() => onAbrirModal('cad-prefeitura')}>
            Alterar Cad. Prefeitura
          </Button>
          <Button variant="outline-secondary" onClick={() => onAbrirModal('ordem-pagamento')}>
            Alterar Ordem Pagamento
          </Button>
        </div>
      )}

      {carregando ? (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <Table bordered hover size="sm" responsive style={{ fontSize: 12, marginBottom: 0 }}>
            <thead>
              <tr>
                <th style={{ width: 36, textAlign: 'center' }}>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Selecionar / desmarcar todos</Tooltip>}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={todosSelecionados}
                      onChange={onToggleTodos}
                      disabled={imoveis.length === 0}
                    />
                  </OverlayTrigger>
                </th>
                <th style={thStyle} onClick={() => onOrdenacao('empreendimento')}>
                  Empreendimento {iconeOrdenacao('empreendimento')}
                </th>
                <th style={thStyle} onClick={() => onOrdenacao('codigo_ativo')}>
                  Código {iconeOrdenacao('codigo_ativo')}
                </th>
                <th style={thStyle} onClick={() => onOrdenacao('quadra')}>
                  QD/LT {iconeOrdenacao('quadra')}
                </th>
                <th style={thStyle} onClick={() => onOrdenacao('status_imovel')}>
                  Status {iconeOrdenacao('status_imovel')}
                </th>
                <th style={thStyle} onClick={() => onOrdenacao('data_ultima_rescisao')}>
                  Data Última Rescisão {iconeOrdenacao('data_ultima_rescisao')}
                </th>
                <th style={thStyle} onClick={() => onOrdenacao('grupo_empresa')}>
                  GE {iconeOrdenacao('grupo_empresa')}
                </th>
                <th style={thStyle} onClick={() => onOrdenacao('cliente')}>
                  Cliente {iconeOrdenacao('cliente')}
                </th>
                <th style={thStyle} onClick={() => onOrdenacao('nome_cliente')}>
                  Nome Cliente {iconeOrdenacao('nome_cliente')}
                </th>
                <th style={thStyle} onClick={() => onOrdenacao('codigo_boleto')}>
                  Boleto {iconeOrdenacao('codigo_boleto')}
                </th>
                <th style={thStyle} onClick={() => onOrdenacao('data_venda')}>
                  Data de Venda {iconeOrdenacao('data_venda')}
                </th>
                <th style={{ width: 60, textAlign: 'center', fontSize: 12 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {imoveis.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center text-muted py-3">
                    Nenhum imóvel encontrado.
                  </td>
                </tr>
              ) : (
                imoveis.map((item) => {
                  const qdLt = item.quadra || item.lote
                    ? `${item.quadra || ''}${item.lote ? `/${item.lote}` : ''}`
                    : '—';
                  return (
                    <tr
                      key={item.codigo_ativo}
                      className={movTipoClass(item.mov_tipo)}
                      onContextMenu={(ev) => abrirMenuContexto(ev, item)}
                    >
                      <td style={{ textAlign: 'center' }}>
                        <Form.Check
                          type="checkbox"
                          checked={selecionados.includes(item.codigo_ativo)}
                          onChange={() => onToggleSelecionado(item.codigo_ativo)}
                        />
                      </td>
                      <td>{item.empreendimento || '—'}</td>
                      <td>{item.codigo_ativo}</td>
                      <td>{qdLt}</td>
                      <td>{item.status_imovel || '—'}</td>
                      <td>{formatarData(item.data_ultima_rescisao)}</td>
                      <td>{item.grupo_empresa != null ? item.grupo_empresa : '—'}</td>
                      <td>{item.cliente != null ? item.cliente : '—'}</td>
                      <td>{item.nome_cliente || '—'}</td>
                      <td>{item.codigo_boleto != null ? item.codigo_boleto : '—'}</td>
                      <td>{formatarData(item.data_venda)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <OverlayTrigger
                          placement="left"
                          overlay={<Tooltip>Ver demais informações</Tooltip>}
                        >
                          <button
                            type="button"
                            className={styles.btnLupa}
                            onClick={() => onVerDetalhes(item)}
                          >
                            <FaSearch color="#0747a6" size={18} />
                          </button>
                        </OverlayTrigger>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      )}

      {renderizarPaginacao()}

      {/* PopupMenu1 (Delphi) — replicado como menu de contexto flutuante. */}
      {menuCtx && (
        <ul
          ref={menuRef}
          className={styles.menuContexto}
          style={{
            top: menuPos ? menuPos.top : menuCtx.y,
            left: menuPos ? menuPos.left : menuCtx.x,
          }}
        >
          {MENU_CONTEXTO_ITENS.map((it) => {
            let disabled = false;
            if (it.requerCondominio && !habilitaCondominio) disabled = true;
            if (it.requerRescisao && !podeImprimirRescisao(menuCtx.imovel)) disabled = true;
            return (
              <li
                key={it.id}
                className={`${styles.menuItem} ${disabled ? styles.menuItemDisabled : ''}`}
                onClick={() => !disabled && handleMenuItemClick(it)}
              >
                {it.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default GridGestaoIPTU;
